import type { BoundingBox } from "@/types";

const JPEG_QUALITY = 0.9;
const BBOX_COORD_MAX = 1000;

// offset.x / offset.y are 0..1 positions (CSS object-position style) indicating
// how the overflowing axis is panned: 0 = fully left/top, 1 = fully right/bottom,
// 0.5 = centered. The non-overflowing axis ignores its value.
export interface CropOffset {
  x: number;
  y: number;
}

export interface CropWindow {
  srcX: number;
  srcY: number;
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
}

export function computeCropWindow(
  naturalWidth: number,
  naturalHeight: number,
  aspectRatio: number,
  offset: CropOffset
): CropWindow {
  const imgAspect = naturalWidth / naturalHeight;
  let width: number;
  let height: number;

  if (imgAspect > aspectRatio) {
    height = naturalHeight;
    width = Math.round(height * aspectRatio);
  } else {
    width = naturalWidth;
    height = Math.round(width / aspectRatio);
  }

  const srcX = Math.round((naturalWidth - width) * clamp01(offset.x));
  const srcY = Math.round((naturalHeight - height) * clamp01(offset.y));

  return { srcX, srcY, width, height, naturalWidth, naturalHeight };
}

export function cropImageToAspect(
  file: File,
  aspectRatio: number,
  offset: CropOffset
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const window = computeCropWindow(img.naturalWidth, img.naturalHeight, aspectRatio, offset);
      const canvas = document.createElement("canvas");
      canvas.width = window.width;
      canvas.height = window.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      ctx.drawImage(
        img,
        window.srcX,
        window.srcY,
        window.width,
        window.height,
        0,
        0,
        window.width,
        window.height
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Could not create blob"));
            return;
          }
          const cropped = new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
            type: "image/jpeg",
          });
          resolve(cropped);
        },
        "image/jpeg",
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image"));
    };

    img.src = url;
  });
}

// Transforms a bounding box from the pre-crop coord space (0..1000 on the
// original image) to the post-crop coord space (0..1000 on the cropped image).
// Returns null if the bbox has no overlap with the crop window.
export function transformBoundingBoxForCrop(
  bbox: BoundingBox,
  window: CropWindow
): BoundingBox | null {
  const srcXNorm = (window.srcX / window.naturalWidth) * BBOX_COORD_MAX;
  const srcYNorm = (window.srcY / window.naturalHeight) * BBOX_COORD_MAX;
  const widthNorm = (window.width / window.naturalWidth) * BBOX_COORD_MAX;
  const heightNorm = (window.height / window.naturalHeight) * BBOX_COORD_MAX;

  const xMin = ((bbox.xMin - srcXNorm) * BBOX_COORD_MAX) / widthNorm;
  const yMin = ((bbox.yMin - srcYNorm) * BBOX_COORD_MAX) / heightNorm;
  const xMax = ((bbox.xMax - srcXNorm) * BBOX_COORD_MAX) / widthNorm;
  const yMax = ((bbox.yMax - srcYNorm) * BBOX_COORD_MAX) / heightNorm;

  const clippedXMin = Math.max(0, Math.min(BBOX_COORD_MAX, xMin));
  const clippedYMin = Math.max(0, Math.min(BBOX_COORD_MAX, yMin));
  const clippedXMax = Math.max(0, Math.min(BBOX_COORD_MAX, xMax));
  const clippedYMax = Math.max(0, Math.min(BBOX_COORD_MAX, yMax));

  if (clippedXMax - clippedXMin < 1 || clippedYMax - clippedYMin < 1) return null;

  return {
    xMin: Math.round(clippedXMin),
    yMin: Math.round(clippedYMin),
    xMax: Math.round(clippedXMax),
    yMax: Math.round(clippedYMax),
  };
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
